import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Data } from '@angular/router';
import { BlUrlQueryKey } from '@blcloud/bl-ng-common';
import { UrlUtils } from '@blcloud/bl-common';
import { DeviceSessionService } from '@blcloud/bl-ng-share-module';
import { Observable } from 'rxjs/Observable';
import { of as RxOf } from 'rxjs/observable/of';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { LoginPageService } from '../../../page/login-page/login-page.service';
import { BlAuthService } from '@blcloud/bl-ng-common';
import { MasterPrecheckResource } from '@blcloud/bl-ng-resource';
import { LoginResourceService } from '@blcloud/bl-ng-share-module';
import { isNil as _isNil, map as _map } from 'lodash';

/**
 * 自動ログインクラス
 */
@Injectable()
export class AutoLoginGuard implements CanActivate {

  /**
   * コンストラクタ
   */
  constructor(
    private loginPageService: LoginPageService,
    private deviceSessionSession: DeviceSessionService,
    private blAuthService: BlAuthService,
    private masterPrecheckResource: MasterPrecheckResource,
    private loginResourceService: LoginResourceService,
  ) { }

  /**
   * アクティベート可能であるか判定します。
   * @returns 判定結果
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.doAutoLogin(route.data);
  }

  /**
   * 自動ログイン
   * アクセス先URLのクエリパラメータに認証情報が含まれている場合、
   * 認証情報をcookieに保存した後にログイン画面を表示せずにアクセス先へリダイレクトする
   */
  private doAutoLogin(data: Data): Observable<boolean> {

    const rtnUrlParam = location.href.match(/rtnUrl=(.*)/);
    const rtnUrl = rtnUrlParam != null ? decodeURIComponent(rtnUrlParam[1]) : null;
    // ログイン画面へのリダイレクトは防止する

    const returnUrl = rtnUrl != null && !rtnUrl.split('?')[0].match(/menu\/login$/) ? rtnUrl : null;
    if (returnUrl === null) {
      // アクセス先URLの指定が無いまたは、アクセス先URLがログイン画面である場合は、自動ログインしない
      return this.isAuthRedirect(data);
    }

    // クエリパラメータから認証情報を取得
    const accessTokenRegex = BlUrlQueryKey.AccessToken + '=(.*?)(&|$)';
    const idTokenRegex = BlUrlQueryKey.IdToken + '=(.*?)(&|$)';
    const accessTokenMatchArray = returnUrl.match(accessTokenRegex);
    const idTokenMatchArray = returnUrl.match(idTokenRegex);
    if (!accessTokenMatchArray || !idTokenMatchArray) {
      // 認証情報がクエリパラメータに含まれていない場合は、自動ログインしない
      return this.isAuthRedirect(data);
    }
    const accessTokenOfQuery = accessTokenMatchArray[0];
    const idTokenOfQuery = idTokenMatchArray[0];
    const accessToken = accessTokenMatchArray[1];
    const idToken = idTokenMatchArray[1];

    // アクセス先URLから認証情報のクエリパラメータを削除
    const accurateReturnUrl = returnUrl.replace(accessTokenOfQuery, '').replace(idTokenOfQuery, '');
    try {
      return this.loginPageService.clearLoginCookie()
        .pipe(
          map(() => this.loginPageService.setLoginCookie([accessToken, idToken])),
          mergeMap(() => this.deviceSessionSession.createDeviceSession()),
          map((success) => {
            console.log(success);
            if (accurateReturnUrl && UrlUtils.isSafeReturnUrl(accurateReturnUrl, false)) {
              // 指定されたアクセス先へリダイレクト
              window.open(accurateReturnUrl, '_self');
              return false;
            } else {
              // リダイレクトしない
              return true;
            }
          }),
          catchError((err) => {
            console.log(err);
            return RxOf(true);
          }),
        );
    } catch (err) {
      console.log(err);
      // 端末セッションの登録に失敗した場合は、リダイレクトしない
      return RxOf(true);
    }
  }

  /**
   * 認証リダイレクトチェック
   * @param data データ
   */
  private isAuthRedirect(data: Data): Observable<boolean> {
    if (_isNil(data) || _isNil(data.redirectPath)) {
      console.error('AuthRedirectを利用するには `data: { redirectPath: string }` をRouteに指定する必要があります。');
      return RxOf(true);
    }
    // パスワード強制変更画面から「戻る」または「再ログイン」、オートログインをブロックしてCookieを消去する
    if (localStorage.getItem('isPasswordChangeRequired') === 'true') {
      localStorage.removeItem('isPasswordChangeRequired'); // 消去する
      return this.loginPageService.clearLoginCookie().pipe(
        map(() => true), // trueを返してリダイレクトを止め、ログイン画面を表示させる
        catchError((err) => {
          console.error('戻るボタン押下時のログインCookieクリアに失敗しました:', err);
          return RxOf(true);
        })
      );
    }

    try {
      // 認証情報の有無を確認
      const query = {};
      const result = this.blAuthService.auth(query);

      if (result) {
        // 認証が有効か確認するためにAPI呼び出し
        return this.loginResourceService.getAllServiceAvailabilityInfo().pipe(
          mergeMap(infos => {
            const serviceIdList = _map(infos, info => info.serviceId);
            return this.masterPrecheckResource.post({ serviceIdList });
          }),
          mergeMap(
            () => {
              // ログアウトが指定されている場合はログアウト処理を実行
              const logout = location.href.match(/logout=(.*)/);
              if (logout) {
                return this.loginResourceService.logout(false).pipe(
                  map(() => {
                    return true;
                  })
                );
              } else {
                return RxOf(false)
              }
            }
          ),
          map((isLogout) => {
            // ログアウトした場合は続行
            if (isLogout) {
              return true;
            }

            // URLクエリで戻り先が指定されている場合はURLクエリを優先
            // ログインページは無効
            const rtnUrlParam = location.href.match(/rtnUrl=(.*)/);
            const rtnUrl = rtnUrlParam != null ? decodeURIComponent(rtnUrlParam[1]) : null;
            if (
              rtnUrl !== null &&
              !rtnUrl.split('?')[0].match(/menu\/login$/) &&
              UrlUtils.isSafeReturnUrl(rtnUrl, false)
            ) {
              location.href = rtnUrl;
              return false;
            }

            location.href = data.redirectPath;
            return false;
          }),
          catchError(() => {
            // 認証エラーを含めて例外発生時は続行（例外処理は画面側に任せる）
            return RxOf(true);
          })
        );
      }
    } catch (err) {
      console.log(err);
      return RxOf(true);
    }

    return RxOf(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/guard/auto-login/auto-login.guard.ts